/**
 * Visuales de clasificación por habilidades (Sprint 314 D; REQ-MINT-006).
 *
 * - **Mapa de calor** modelos × ámbitos (percentil; «sin dato» explícito).
 * - **Ranking por ámbito** con marcas P75/P25 y «top N % del conjunto».
 * - **Radar comparativo multi-modelo** (2–4; sin dato no se imputa).
 *
 * Los datos salen del contrato de comparación (`POST /compare`); la procedencia
 * (nº de benchmarks y fecha del dato más antiguo) acompaña a cada celda. La UI
 * no filtra permisos: lo que no llega, no se pinta (SEC-022).
 */

import { useEffect, useMemo, useState, type CSSProperties } from 'react';
import {
  PolarAngleAxis,
  PolarGrid,
  PolarRadiusAxis,
  Radar,
  RadarChart,
  ResponsiveContainer,
  Tooltip,
} from 'recharts';
import { Card, CardContent } from '@/components/ui';
import { useI18n } from '@/i18n';
import { formatDate } from '../lib/pricing';
import {
  defaultRadarSelection,
  domainRanking,
  radarSeries,
  skillDomains,
  skillMatrix,
  sortSkillRows,
} from '../lib/skills';
import type { OfferingIntelligence } from '../types';

const RADAR_COLORS = ['#2563eb', '#16a34a', '#d97706', '#dc2626'] as const;
const RADAR_MAX = 4;

function domainLabel(
  t: (key: string, params?: Record<string, string | number>) => string,
  domain: string,
): string {
  const key = `modelIntel.domains.${domain}`;
  const label = t(key);
  return label === key ? domain : label;
}

/** Escala de color accesible por percentil (celda «sin dato» sin fondo). */
function cellStyle(score: number | null): CSSProperties {
  if (score === null) return {};
  const alpha = 0.12 + (0.68 * Math.max(0, Math.min(100, score))) / 100;
  return { backgroundColor: `rgba(37, 99, 235, ${alpha.toFixed(3)})` };
}

export function SkillVisuals({ eligible }: { eligible: OfferingIntelligence[] }) {
  const { t } = useI18n();
  const domains = useMemo(() => skillDomains(eligible), [eligible]);
  const [sortDomain, setSortDomain] = useState('');
  const rows = useMemo(
    () => sortSkillRows(skillMatrix(eligible), sortDomain || undefined),
    [eligible, sortDomain],
  );
  const [rankingDomain, setRankingDomain] = useState('');
  const activeRankingDomain = domains.includes(rankingDomain) ? rankingDomain : (domains[0] ?? '');
  const ranking = useMemo(
    () => (activeRankingDomain ? domainRanking(eligible, activeRankingDomain) : null),
    [eligible, activeRankingDomain],
  );
  const [radarSelection, setRadarSelection] = useState<string[]>(() =>
    defaultRadarSelection(eligible),
  );
  useEffect(() => {
    setRadarSelection(defaultRadarSelection(eligible));
  }, [eligible]);
  const radar = useMemo(() => radarSeries(eligible, radarSelection), [eligible, radarSelection]);
  const radarCandidates = useMemo(
    () => eligible.filter((item) => (item.benchmarks?.domains?.length ?? 0) > 0),
    [eligible],
  );

  const toggleRadar = (key: string, checked: boolean) => {
    setRadarSelection((current) => {
      if (checked) {
        if (current.includes(key) || current.length >= RADAR_MAX) return current;
        return [...current, key];
      }
      return current.filter((item) => item !== key);
    });
  };

  if (!domains.length) {
    return (
      <Card>
        <CardContent className="p-4">
          <div className="mb-1 text-sm font-medium">{t('modelIntel.compare.skills.title')}</div>
          <p className="text-sm text-gray-500" data-testid="skills-empty">
            {t('modelIntel.compare.skills.empty')}
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent className="space-y-6 p-4" data-testid="skills-visuals">
        <div>
          <div className="text-sm font-medium">{t('modelIntel.compare.skills.title')}</div>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {t('modelIntel.compare.skills.subtitle')}
          </p>
        </div>

        <section>
          <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
            <h3 className="text-sm font-medium">{t('modelIntel.compare.skills.heatmapTitle')}</h3>
            <label className="text-xs text-gray-500 dark:text-gray-400">
              {t('modelIntel.compare.skills.sortLabel')}
              <select
                className="ml-2 rounded border bg-background px-2 py-1 text-xs"
                value={sortDomain}
                onChange={(event) => setSortDomain(event.target.value)}
                data-testid="skills-sort"
              >
                <option value="">{t('modelIntel.compare.skills.sortAverage')}</option>
                {domains.map((domain) => (
                  <option key={domain} value={domain}>
                    {domainLabel(t, domain)}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="skills-heatmap">
              <thead>
                <tr className="border-b text-left text-xs text-gray-500 dark:text-gray-400">
                  <th className="py-2 pr-3">{t('modelIntel.compare.skills.heatmapModel')}</th>
                  {domains.map((domain) => (
                    <th key={domain} className="py-2 pr-3 text-center">
                      {domainLabel(t, domain)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr
                    key={row.key}
                    className="border-b last:border-0"
                    data-testid={`skills-row-${row.key}`}
                  >
                    <td className="py-2 pr-3">
                      <div className="font-medium">{row.label}</div>
                      <div className="text-xs capitalize text-gray-500 dark:text-gray-400">
                        {row.provider}
                      </div>
                    </td>
                    {row.cells.map((cell) => (
                      <td
                        key={cell.domain}
                        className="py-2 pr-3 text-center"
                        data-testid={`skills-cell-${row.key}-${cell.domain}`}
                      >
                        {cell.score === null ? (
                          <span className="inline-block rounded bg-gray-100 px-2 py-0.5 text-xs text-gray-500 dark:bg-gray-800 dark:text-gray-400">
                            {t('modelIntel.compare.skills.noData')}
                          </span>
                        ) : (
                          <span
                            className="inline-block rounded px-2 py-0.5 font-medium tabular-nums"
                            style={cellStyle(cell.score)}
                            title={t('modelIntel.compare.skills.cellTooltip', {
                              count: cell.benchmarksCount ?? 0,
                              date: cell.oldestPublishedAt
                                ? formatDate(cell.oldestPublishedAt)
                                : t('modelIntel.compare.skills.noData'),
                            })}
                          >
                            {cell.score.toFixed(0)}
                          </span>
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <section>
          <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
            <h3 className="text-sm font-medium">{t('modelIntel.compare.skills.rankingTitle')}</h3>
            <label className="text-xs text-gray-500 dark:text-gray-400">
              {t('modelIntel.compare.skills.rankingDomainLabel')}
              <select
                className="ml-2 rounded border bg-background px-2 py-1 text-xs"
                value={activeRankingDomain}
                onChange={(event) => setRankingDomain(event.target.value)}
                data-testid="skills-ranking-domain"
              >
                {domains.map((domain) => (
                  <option key={domain} value={domain}>
                    {domainLabel(t, domain)}
                  </option>
                ))}
              </select>
            </label>
          </div>
          {!ranking || ranking.entries.length === 0 ? (
            <p className="text-sm text-gray-500" data-testid="skills-ranking-empty">
              {t('modelIntel.compare.skills.rankingEmpty')}
            </p>
          ) : (
            <>
              <div className="mb-2 flex flex-wrap gap-x-4 text-xs text-gray-500 dark:text-gray-400">
                {ranking.p75 !== null && ranking.p25 !== null && (
                  <span data-testid="skills-ranking-thresholds">
                    {t('modelIntel.compare.skills.thresholds', {
                      p75: ranking.p75.toFixed(0),
                      p25: ranking.p25.toFixed(0),
                    })}
                  </span>
                )}
                {ranking.oldestPublishedAt && (
                  <span>
                    {t('modelIntel.compare.skills.oldest', {
                      date: formatDate(ranking.oldestPublishedAt),
                    })}
                  </span>
                )}
              </div>
              <ol className="space-y-2" data-testid="skills-ranking">
                {ranking.entries.map((entry) => (
                  <li key={entry.key} data-testid={`skills-rank-${entry.key}`}>
                    <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
                      <span className="flex items-center gap-2">
                        <span className="font-medium">{entry.label}</span>
                        {entry.strength && (
                          <span className="rounded bg-green-100 px-1.5 py-0.5 text-xs text-green-700 dark:bg-green-900/40 dark:text-green-300">
                            {t('modelIntel.compare.skills.strength')}
                          </span>
                        )}
                        {entry.gap && (
                          <span className="rounded bg-amber-100 px-1.5 py-0.5 text-xs text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">
                            {t('modelIntel.compare.skills.gap')}
                          </span>
                        )}
                      </span>
                      <span className="tabular-nums text-gray-500 dark:text-gray-400">
                        {entry.score.toFixed(0)} ·{' '}
                        {t('modelIntel.compare.skills.rankingTop', { pct: entry.topPct })}
                      </span>
                    </div>
                    <div className="mt-1 h-1.5 rounded bg-gray-100 dark:bg-gray-800">
                      <div
                        className="h-1.5 rounded bg-primary"
                        style={{ width: `${Math.max(0, Math.min(100, entry.score))}%` }}
                      />
                    </div>
                  </li>
                ))}
              </ol>
            </>
          )}
        </section>

        <section>
          <h3 className="text-sm font-medium">{t('modelIntel.compare.skills.radarTitle')}</h3>
          <p className="mb-2 text-xs text-gray-500 dark:text-gray-400">
            {t('modelIntel.compare.skills.radarHint')}
          </p>
          <div className="mb-3 flex flex-wrap gap-x-4 gap-y-1 text-sm" data-testid="skills-radar-picker">
            {radarCandidates.map((item) => {
              const key = item.offering.sheet_id;
              const checked = radarSelection.includes(key);
              return (
                <label key={key} className="flex items-center gap-1.5">
                  <input
                    type="checkbox"
                    checked={checked}
                    disabled={!checked && radarSelection.length >= RADAR_MAX}
                    onChange={(event) => toggleRadar(key, event.target.checked)}
                    data-testid={`skills-radar-${key}`}
                  />
                  {item.offering.model_ref ?? item.offering.provider_model_id}
                </label>
              );
            })}
          </div>
          {radar.series.length === 0 ? (
            <p className="text-sm text-gray-500" data-testid="skills-radar-empty">
              {t('modelIntel.compare.skills.radarEmpty')}
            </p>
          ) : (
            <div data-testid="skills-radar-chart">
              <ResponsiveContainer width="100%" height={280}>
                <RadarChart data={radar.rows} outerRadius="70%">
                  <PolarGrid />
                  <PolarAngleAxis
                    dataKey="domain"
                    tickFormatter={(value: string) => domainLabel(t, value)}
                  />
                  <PolarRadiusAxis domain={[0, 100]} />
                  <Tooltip />
                  {radar.series.map((series, index) => (
                    <Radar
                      key={series.key}
                      name={series.label}
                      dataKey={series.key}
                      stroke={RADAR_COLORS[index % RADAR_COLORS.length]}
                      fill={RADAR_COLORS[index % RADAR_COLORS.length]}
                      fillOpacity={0.18}
                    />
                  ))}
                </RadarChart>
              </ResponsiveContainer>
            </div>
          )}
        </section>
      </CardContent>
    </Card>
  );
}
